import { RealtimePlaybackLifecycle } from './realtime-playback-lifecycle.js';
import { connectSdkRealtimeWebSocket } from './realtime-conversation-websocket.js';
import { RealtimeTransportWatchdog, } from '../runtime/realtime-transport-watchdog.js';
const EXPERTS_ALIVE_API_BASE_URL = 'https://pa4mo8ntn0.execute-api.us-east-2.amazonaws.com';
/**
 * Opens an Experts Alive Realtime turn controller with echo-safe microphone
 * gating. The SDK owns the safe session policy and manual response creation;
 * host applications must not duplicate VAD or speaker-echo handling.
 */
export async function connectSdkRealtime(options) {
    if (/\bFirefox\//.test(navigator.userAgent)) {
        return connectSdkRealtimeWebSocket(options);
    }
    return connectSdkRealtimeWebRtc(options);
}
async function connectSdkRealtimeWebRtc(options) {
    options.onState('connecting');
    const sessionEndpoint = options.sessionEndpoint ?? `${EXPERTS_ALIVE_API_BASE_URL}/sdk/session`;
    const headers = {
        'Content-Type': 'application/json',
    };
    if (options.deploymentKey)
        headers.Authorization = `Bearer ${options.deploymentKey}`;
    const sessionResponse = await fetch(sessionEndpoint, {
        body: JSON.stringify({
            group: options.group,
            humanId: options.humanId,
            language: options.language,
            platform: options.platform,
            platformId: options.platformId ?? window.location.origin,
            resolution: options.resolution,
            tutorName: options.tutorName,
            voice: options.voice,
        }),
        headers,
        method: 'POST',
    });
    const session = (await sessionResponse
        .json()
        .catch(() => ({})));
    if (!sessionResponse.ok)
        throw new Error(session.message ?? 'Experts Alive could not start this session.');
    const ephemeralKey = typeof session.client_secret === 'string'
        ? session.client_secret
        : (session.client_secret?.value ?? session.value);
    if (!ephemeralKey)
        throw new Error('The Experts Alive runtime did not return a session key.');
    const pc = new RTCPeerConnection();
    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.setAttribute('playsinline', 'true');
    audio.hidden = true;
    document.body.appendChild(audio);
    const microphoneEnabled = options.microphone !== false;
    const inputStream = microphoneEnabled
        ? await navigator.mediaDevices.getUserMedia({
            audio: {
                autoGainControl: true,
                echoCancellation: true,
                noiseSuppression: true,
            },
            video: false,
        })
        : new MediaStream();
    const AudioContextConstructor = window.AudioContext ??
        window
            .webkitAudioContext;
    if (!AudioContextConstructor) {
        inputStream.getTracks().forEach((track) => track.stop());
        audio.remove();
        pc.close();
        throw new Error('Web Audio is not available in this browser.');
    }
    const audioContext = new AudioContextConstructor();
    const micSource = microphoneEnabled
        ? audioContext.createMediaStreamSource(inputStream)
        : null;
    const micGate = audioContext.createGain();
    const peerInput = audioContext.createMediaStreamDestination();
    micGate.gain.value = 0;
    micSource?.connect(micGate);
    micGate.connect(peerInput);
    peerInput.stream
        .getAudioTracks()
        .forEach((track) => pc.addTrack(track, peerInput.stream));
    const setMicEnabled = (enabled) => {
        if (audioContext.state === 'suspended')
            void audioContext.resume();
        if (!microphoneEnabled)
            return;
        micGate.gain.setTargetAtTime(enabled ? 1 : 0, audioContext.currentTime, 0.015);
        inputStream.getAudioTracks().forEach((track) => {
            track.enabled = true;
        });
    };
    let detachMotionOutput = null;
    pc.ontrack = (event) => {
        const outputStream = event.streams[0] ?? new MediaStream([event.track]);
        audio.srcObject = outputStream;
        detachMotionOutput?.();
        detachMotionOutput = null;
        if (options.motionRuntime) {
            try {
                detachMotionOutput = options.motionRuntime.attachOutputMediaStream(outputStream);
            }
            catch (cause) {
                const message = cause instanceof Error ? cause.message : String(cause);
                options.onError(`Digital Human audio guide could not start: ${message}`);
            }
        }
        options.onOutputStream?.(outputStream);
        void audio.play().catch(() => undefined);
    };
    const channel = pc.createDataChannel('oai-events');
    let assistantText = '';
    let responseInProgress = false;
    let userListening = false;
    let listeningTimer = 0;
    let sessionPolicyTimer = 0;
    let sessionPolicyReady = false;
    let disconnected = false;
    let transportStatsTimer = 0;
    let transportInterrupted = false;
    const send = (payload) => {
        if (channel.readyState === 'open')
            channel.send(JSON.stringify(payload));
    };
    const clearListeningTimer = () => {
        if (listeningTimer)
            window.clearTimeout(listeningTimer);
        listeningTimer = 0;
    };
    const startListening = (delayMs = 0) => {
        if (!microphoneEnabled)
            return false;
        clearListeningTimer();
        setMicEnabled(false);
        userListening = false;
        if (!sessionPolicyReady || disconnected)
            return false;
        listeningTimer = window.setTimeout(() => {
            listeningTimer = 0;
            if (responseInProgress ||
                playback.isPlaybackActive() ||
                channel.readyState !== 'open')
                return;
            send({ type: 'input_audio_buffer.clear' });
            userListening = true;
            setMicEnabled(true);
            options.onState('listening');
        }, delayMs);
        return true;
    };
    const playback = new RealtimePlaybackLifecycle({
        onPlaybackStarted: () => {
            clearListeningTimer();
            userListening = false;
            responseInProgress = true;
            setMicEnabled(false);
            transportWatchdog.reset();
            options.motionRuntime?.beginSpeaking({
                holdThroughSilence: true,
                source: 'realtime-speaker-drain',
            });
            options.onState('speaking');
        },
        onPlaybackStopped: () => {
            assistantText = '';
            options.motionRuntime?.updateSpeechGuide('');
            options.motionRuntime?.returnToIdle('realtime-output-buffer-stopped');
            responseInProgress = false;
            setMicEnabled(false);
            options.onState('ready');
            if (!transportInterrupted && options.autoListen !== false)
                startListening(650);
        },
    });
    const transportWatchdog = new RealtimeTransportWatchdog({
        onInterrupted: (reason) => {
            if (disconnected || transportInterrupted)
                return;
            transportInterrupted = true;
            setMicEnabled(false);
            playback.interrupt();
            options.onState('error');
            options.onTransportInterrupted?.(reason);
            if (!options.onTransportInterrupted) {
                options.onError('Audio connection interrupted. Reconnect to continue.');
            }
        },
        onRecovered: () => undefined,
        onSuspected: () => undefined,
    });
    const inspectTransport = async () => {
        if (disconnected || !playback.isPlaybackActive())
            return;
        try {
            const report = await pc.getStats();
            report.forEach((item) => {
                const stat = item;
                const kind = stat.kind ?? stat.mediaType;
                if (stat.type !== 'inbound-rtp' || kind !== 'audio')
                    return;
                transportWatchdog.observe({
                    atMs: performance.now(),
                    bytesReceived: Number(stat.bytesReceived ?? 0),
                    concealedSamples: Number(stat.concealedSamples ?? 0),
                    iceConnectionState: pc.iceConnectionState,
                    outputActive: true,
                    packetsReceived: Number(stat.packetsReceived ?? 0),
                    peerConnectionState: pc.connectionState,
                });
            });
        }
        catch {
            // A following peer or data-channel event surfaces a hard failure.
        }
    };
    const observeHardTransportFailure = () => {
        if (!playback.isPlaybackActive() || transportInterrupted)
            return;
        if (pc.connectionState !== 'failed' && pc.iceConnectionState !== 'failed')
            return;
        transportWatchdog.observe({
            atMs: performance.now(),
            bytesReceived: 0,
            iceConnectionState: pc.iceConnectionState,
            outputActive: true,
            packetsReceived: 0,
            peerConnectionState: pc.connectionState,
        });
    };
    pc.addEventListener('connectionstatechange', observeHardTransportFailure);
    pc.addEventListener('iceconnectionstatechange', observeHardTransportFailure);
    channel.addEventListener('open', () => {
        setMicEnabled(false);
        send({
            session: {
                type: 'realtime',
                instructions: options.conversationInstructions,
                audio: {
                    input: {
                        turn_detection: {
                            create_response: false,
                            eagerness: 'high',
                            interrupt_response: false,
                            type: 'semantic_vad',
                        },
                    },
                },
            },
            type: 'session.update',
        });
        sessionPolicyTimer = window.setTimeout(() => {
            sessionPolicyTimer = 0;
            if (sessionPolicyReady || disconnected)
                return;
            setMicEnabled(false);
            options.onState('error');
            options.onError('The safe microphone policy could not be confirmed. Reconnect to continue.');
        }, 4_000);
    });
    channel.addEventListener('message', (message) => {
        try {
            const event = JSON.parse(message.data);
            const type = event.type ?? '';
            playback.handleServerEvent(type);
            if (type === 'session.updated') {
                if (sessionPolicyTimer)
                    window.clearTimeout(sessionPolicyTimer);
                sessionPolicyTimer = 0;
                sessionPolicyReady = true;
                options.onState('ready');
                if (options.greetingPrompt) {
                    responseInProgress = true;
                    send({
                        response: { instructions: options.greetingPrompt },
                        type: 'response.create',
                    });
                }
                else if (options.autoListen !== false) {
                    startListening();
                }
            }
            if (type === 'response.created') {
                responseInProgress = true;
                userListening = false;
                setMicEnabled(false);
                assistantText = '';
                options.motionRuntime?.updateSpeechGuide('');
                options.onAssistantText('');
            }
            if (type.includes('input_audio_buffer.speech_started') &&
                userListening &&
                !playback.isPlaybackActive()) {
                options.onState('listening');
            }
            if (type.includes('input_audio_buffer.committed') &&
                userListening &&
                !responseInProgress &&
                !playback.isPlaybackActive()) {
                userListening = false;
                responseInProgress = true;
                setMicEnabled(false);
                send({ type: 'response.create' });
            }
            if (type.includes('conversation.item.input_audio_transcription.completed') &&
                event.transcript) {
                options.onUserText(event.transcript.trim());
            }
            if ((type.includes('response.output_audio_transcript.delta') ||
                type.includes('response.audio_transcript.delta')) &&
                event.delta) {
                assistantText += event.delta;
                options.motionRuntime?.updateSpeechGuide(assistantText);
                options.onAssistantText(assistantText);
            }
            if ((type.includes('response.output_audio_transcript.done') ||
                type.includes('response.audio_transcript.done')) &&
                event.transcript) {
                assistantText = event.transcript.trim();
                options.motionRuntime?.updateSpeechGuide(assistantText);
                options.onAssistantText(assistantText);
            }
            if (type === 'response.done') {
                // Generation is complete; output_audio_buffer.stopped ends playback.
                responseInProgress = false;
                if (!playback.isPlaybackActive() && options.autoListen !== false)
                    startListening(650);
            }
            if (event.error?.message) {
                userListening = false;
                setMicEnabled(false);
                options.onState('error');
                options.onError(event.error.message);
            }
        }
        catch {
            // Realtime emits lifecycle events that do not affect the SDK controller.
        }
    });
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    const model = session.model ?? 'gpt-realtime-2.1-mini';
    const sdpResponse = await fetch(`https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(model)}`, {
        body: offer.sdp ?? '',
        headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            'Content-Type': 'application/sdp',
        },
        method: 'POST',
    });
    const answerSdp = await sdpResponse.text();
    if (!sdpResponse.ok) {
        inputStream.getTracks().forEach((track) => track.stop());
        peerInput.stream.getTracks().forEach((track) => track.stop());
        micSource?.disconnect();
        micGate.disconnect();
        void audioContext.close();
        audio.remove();
        channel.close();
        pc.close();
        throw new Error(answerSdp || 'OpenAI could not connect the realtime audio stream.');
    }
    await pc.setRemoteDescription({ sdp: answerSdp, type: 'answer' });
    transportStatsTimer = window.setInterval(() => {
        if (playback.isPlaybackActive())
            void inspectTransport();
    }, 500);
    return {
        session: {
            humanId: session.humanId,
            resolution: session.resolution,
            watermark: session.watermark,
        },
        beginListening: () => startListening(),
        cancelResponse: () => {
            setMicEnabled(false);
            userListening = false;
            if (responseInProgress)
                send({ type: 'response.cancel' });
            send({ type: 'output_audio_buffer.clear' });
            playback.interrupt();
        },
        disconnect: () => {
            if (disconnected)
                return;
            disconnected = true;
            clearListeningTimer();
            if (sessionPolicyTimer)
                window.clearTimeout(sessionPolicyTimer);
            sessionPolicyTimer = 0;
            sessionPolicyReady = false;
            userListening = false;
            setMicEnabled(false);
            playback.dispose();
            detachMotionOutput?.();
            detachMotionOutput = null;
            options.motionRuntime?.updateSpeechGuide('');
            options.motionRuntime?.returnToIdle('realtime-disconnected');
            transportWatchdog.reset();
            if (transportStatsTimer)
                window.clearInterval(transportStatsTimer);
            transportStatsTimer = 0;
            pc.removeEventListener('connectionstatechange', observeHardTransportFailure);
            pc.removeEventListener('iceconnectionstatechange', observeHardTransportFailure);
            options.onOutputStream?.(null);
            channel.close();
            pc.close();
            inputStream.getTracks().forEach((track) => track.stop());
            peerInput.stream.getTracks().forEach((track) => track.stop());
            micSource?.disconnect();
            micGate.disconnect();
            void audioContext.close();
            audio.srcObject = null;
            audio.remove();
            options.onState('ready');
        },
        sendText: (text) => {
            const value = text.trim();
            if (!value ||
                disconnected ||
                !sessionPolicyReady ||
                responseInProgress ||
                playback.isPlaybackActive() ||
                channel.readyState !== 'open') {
                return false;
            }
            clearListeningTimer();
            userListening = false;
            setMicEnabled(false);
            responseInProgress = true;
            send({
                item: {
                    content: [{ text: value, type: 'input_text' }],
                    role: 'user',
                    type: 'message',
                },
                type: 'conversation.item.create',
            });
            send({ type: 'response.create' });
            options.onUserText(value);
            return true;
        },
    };
}
